import React, { useEffect, useState } from 'react';
import { Info } from 'lucide-react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import SEOHead from '../components/SEOHead';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// About Us page. Renders admin-edited markdown stored in `business_info` under
// the key `about_us_content`. When that key is empty (default install), falls
// back to a friendly auto-generated welcome that uses the configured business
// name. Admins manage the content from Site Design → Static Page Content.
const AboutUs = () => {
  const [businessName, setBusinessName] = useState('our business');
  const [adminContent, setAdminContent] = useState('');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Fetch in parallel so the page always renders quickly even if one
    // endpoint is slow / unavailable.
    Promise.all([
      axios.get(`${API_URL}/business-info/business_name`).catch(() => null),
      axios.get(`${API_URL}/business-info/about_us_content`).catch(() => null),
    ]).then(([nameRes, contentRes]) => {
      if (nameRes?.data?.info_value) setBusinessName(nameRes.data.info_value);
      if (contentRes?.data?.info_value) setAdminContent(contentRes.data.info_value);
      setLoaded(true);
    });
  }, []);

  const hasAdminContent = loaded && adminContent.trim().length > 0;

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <SEOHead
        title="About Us"
        description={`Learn more about ${businessName} — who we are, what we do, and how we work.`}
      />
      <div className="max-w-3xl mx-auto px-4">
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <Info className="text-primary-600" size={32} />
            <h1 className="text-3xl font-bold text-gray-900">About Us</h1>
          </div>

          {hasAdminContent ? (
            <div className="prose max-w-none text-gray-700">
              <ReactMarkdown>{adminContent}</ReactMarkdown>
            </div>
          ) : (
            <div className="prose max-w-none text-gray-700 space-y-4">
              <p>
                Welcome to {businessName}. We are committed to delivering a
                great experience for every client and pet that walks through
                our doors.
              </p>
              <p className="text-sm text-gray-500 italic">
                Admins: customize this page's content (text, headings, images)
                from Site Design &rarr; Static Page Content. You can also
                rename, restyle, or hide this page from Site Design &rarr;
                Navigation Menu Labels &amp; Styling.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AboutUs;
